import { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Collapse,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { RuleModelEditionDialogProps } from "@interfaces/slices";
import { setDialogConfig } from "@store/slices/componentsSlice";
import { useLazyGetModelCompanyRulesQuery, usePostModelCompanyRuleMutation } from "@services/api/model";
import { RuleModel } from "@interfaces/model";
import { NumberFormatValues, NumericFormat } from "react-number-format";
import { RootState } from "@store/store";

type FormValues = {
    category_type: string;
    category_code: string;
    parameter_code: string;
    rule: number | undefined;
};

export const AltaEdicionReglaModelo = (props: RuleModelEditionDialogProps) => {
    const dispatch = useDispatch();

    const [error, setError] = useState<string>("");
    const [rules, setRules] = useState<Array<RuleModel>>([]);
    const [showValue, setShowValue] = useState<boolean>(false);

    const [triggerPostModelCompanyRule] = usePostModelCompanyRuleMutation();
    const [triggerGetModelCompanyRules, getModelCompanyRulesResult] = useLazyGetModelCompanyRulesQuery();

    const userSession = useSelector((state: RootState) => state.session.user);

    const formikInitialValues = {
        category_type: "",
        category_code: "",
        parameter_code: "",
        rule: undefined,
    };

    const formik = useFormik<FormValues>({
        initialValues: formikInitialValues,
        validationSchema: yup.object().shape({
            category_type: yup.string().required("Campo requerido"),
            category_code: yup.string().required("Campo requerido"),
            parameter_code: yup.string().required("Campo requerido"),
            rule: yup.number().notRequired(),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);

                console.log(values);

                await triggerPostModelCompanyRule({
                    taxSystemId: props.taxSystemId,
                    payload: {
                        user: userSession.name.concat(" ", userSession.last_name, " ", userSession.last_name_2),
                        tax_system_id: props.taxSystemId,
                        category_type: values.category_type,
                        category_code: values.category_code,
                        parameter_code: values.parameter_code,
                        value: {
                            rule: values.rule,
                            activated: true,
                        },
                    },
                }).unwrap();

                formik.resetForm({
                    values: {
                        ...values,
                    },
                });
                props.onAcceptClick();
                dispatch(setDialogConfig());
            } catch (error: any) {
                console.log(error);
                if (error.status === 400) {
                    setError(error.data.errors.code[0]);
                    setTimeout(() => {
                        setError("");
                    }, 2500);
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    const loadInfo = async () => {
        const rules = await triggerGetModelCompanyRules(props.taxSystemId).unwrap();
        let filtered = rules
            .filter((rule) => rule.value.activated === false)
            .sort((a, b) => a.category_type.localeCompare(b.category_type));

        setRules(filtered);
    };

    useEffect(() => {
        console.log(formik.errors);
    }, [formik.errors]);

    useEffect(() => {
        if (formik.values.parameter_code) {
            const parameterSelected = rules.find(
                (rule) =>
                    rule.category_type === formik.values.category_type &&
                    rule.category_code === formik.values.category_code &&
                    rule.parameter_code === formik.values.parameter_code,
            );

            setShowValue(typeof parameterSelected?.value.rule === "number");
            if (typeof parameterSelected?.value.rule === "number") {
                formik.setFieldValue("rule", parameterSelected?.value.rule, true);
            }
        }
    }, [formik.values.parameter_code]);

    useEffect(() => {
        if (props.type === "ADD") {
            loadInfo();
        }
    }, [props.type]);

    return (
        <Box
            sx={{
                margin: 4,
                maxHeight: "60vh",
                maxWidth: "350px",
                minWidth: "250px"
            }}>
            <Stack sx={{
                rowGap: 2
            }}>
                <Typography
                    variant="body1"
                    color="main3"
                    sx={{
                        fontWeight: 600,
                        textAlign: "center"
                    }}>
                    {props.type === "ADD" ? "Alta" : "Edición"} de regla
                </Typography>
                <form onSubmit={formik.handleSubmit} noValidate>
                    <Grid container spacing={2} sx={{
                        justifyContent: "space-between"
                    }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel required>Sección</InputLabel>
                            <Select
                                name="category_type"
                                label="Sección"
                                value={formik.values.category_type}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                sx={{ color: "#528CD6", fontWeight: 600 }}
                            >
                                {[...new Set(rules.map((rule) => rule.category_type))].map((category) => (
                                    <MenuItem key={category} value={category}>
                                        {category === "abortives" ? "Abortivos" : "Revisión"}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" fullWidth>
                            <InputLabel required>Categoría</InputLabel>
                            <Select
                                name="category_code"
                                label="Categoría"
                                value={formik.values.category_code}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                sx={{ color: "#528CD6", fontWeight: 600 }}
                            >
                                {Array.from(
                                    new Map(
                                        rules
                                            .filter((rule) => rule.category_type === formik.values.category_type)
                                            .map((rule) => [rule.category_code, rule]),
                                    ).values(),
                                ).map((rule) => (
                                    <MenuItem key={rule.category_code} value={rule.category_code}>
                                        {rule.category_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" fullWidth>
                            <InputLabel required>Parámetro</InputLabel>
                            <Select
                                name="parameter_code"
                                label="Parámetro"
                                value={formik.values.parameter_code}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                sx={{ color: "#528CD6", fontWeight: 600 }}
                            >
                                {rules
                                    .filter(
                                        (rule) =>
                                            rule.category_type === formik.values.category_type &&
                                            rule.category_code === formik.values.category_code,
                                    )
                                    .map((rule) => (
                                        <MenuItem key={rule.parameter_code} value={rule.parameter_code}>
                                            {rule.parameter_name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        {showValue && (
                            <FormControl>
                                <NumericFormat
                                    name="Valor"
                                    label="Valor"
                                    size="small"
                                    displayType="input"
                                    required
                                    sx={{
                                        "& .MuiInputBase-root": { color: "#528CD6", fontWeight: 600 },
                                    }}
                                    thousandSeparator
                                    customInput={TextField}
                                    autoComplete="off"
                                    value={formik.values.rule}
                                    onValueChange={async (event: NumberFormatValues) =>
                                        await formik.setFieldValue("rule", event.floatValue, true)
                                    }
                                    onBlur={formik.handleBlur}
                                />
                            </FormControl>
                        )}
                    </Grid>
                    <Collapse in={error !== ""}>
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    </Collapse>

                    <Stack
                        direction={"row"}
                        sx={{
                            justifyContent: "space-around",
                            mt: 2
                        }}>
                        <Button
                            variant="blue_outlined"
                            onClick={() => dispatch(setDialogConfig())}
                            loading={formik.isSubmitting}
                        >
                            Cancelar
                        </Button>
                        <Button variant="contained" type="submit" disabled={formik.isSubmitting}>
                            Agregar
                        </Button>
                    </Stack>
                </form>
            </Stack>
        </Box>
    );
};
